import React, { useState, useEffect } from 'react';
import { Bot, Play, Pause, Clock, CheckCircle2, AlertCircle, RefreshCw, Calendar } from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../../../config';

interface OptimizationHistory {
  timestamp: string;
  status: string;
  details: {
    audited: number;
    recommendationsGenerated: number;
    recommendationsApplied: number;
    errors: string[];
    changes: Array<{
      campaign: string;
      action: string;
      description: string;
    }>;
  };
}

interface AutomationStatus {
  isRunning: boolean;
  lastRunTime: string | null;
  nextRunTime: string;
  schedulerActive: boolean;
  recentOptimizations: OptimizationHistory[];
}

const AutomationPanel: React.FC = () => {
  const [status, setStatus] = useState<AutomationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchStatus();
    // Odśwież status co 30 sekund
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/google-ads/optimize/status`);
      setStatus(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching automation status:', err);
      setError('Nie udało się pobrać statusu automatyzacji');
    } finally {
      setLoading(false);
    }
  };

  const runNow = async () => {
    if (executing || status?.isRunning) return;
    
    try {
      setExecuting(true);
      setError(null);
      const response = await axios.post(`${API_URL}/api/google-ads/optimize/run-now`);
      
      if (response.data.success) {
        // Odśwież status po 2 sekundach
        setTimeout(fetchStatus, 2000);
        // I ponownie po 10 sekundach
        setTimeout(fetchStatus, 10000);
      }
    } catch (err: any) {
      console.error('Error running optimization:', err);
      setError(err.response?.data?.error || 'Błąd uruchamiania optymalizacji');
    } finally {
      setExecuting(false);
    }
  };

  const toggleScheduler = async () => {
    try {
      const action = status?.schedulerActive ? 'stop' : 'start';
      const response = await axios.post(`${API_URL}/api/google-ads/optimize/scheduler/${action}`);
      
      if (response.data.success) {
        fetchStatus();
      }
    } catch (err) {
      console.error('Error toggling scheduler:', err);
      setError('Błąd zmiany statusu schedulera');
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Nigdy';
    const date = new Date(dateString);
    return date.toLocaleString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Bot className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Automatyczna Optymalizacja</h3>
              <p className="text-sm text-gray-500">Zarządzaj automatycznymi optymalizacjami kampanii</p>
            </div>
          </div>
          <button
            onClick={fetchStatus}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Odśwież status"
          >
            <RefreshCw className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Status */}
      <div className="p-6 space-y-6">
        {error && (
          <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        )}

        {/* Main Controls */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Scheduler Status */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-700">Harmonogram</span>
              <button
                onClick={toggleScheduler}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  status?.schedulerActive
                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                {status?.schedulerActive ? 'Aktywny' : 'Wyłączony'}
              </button>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span className="text-gray-600">Codziennie o:</span>
                <span className="font-medium">{status?.nextRunTime || '09:00'}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-gray-400" />
                <span className="text-gray-600">Ostatnie uruchomienie:</span>
                <span className="font-medium">{formatDate(status?.lastRunTime || null)}</span>
              </div>
            </div>
          </div>

          {/* Manual Run */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-700">Ręczne uruchomienie</span>
              {status?.isRunning && (
                <span className="flex items-center gap-1 text-xs text-amber-600">
                  <div className="w-2 h-2 bg-amber-600 rounded-full animate-pulse"></div>
                  W trakcie
                </span>
              )}
            </div>
            <button
              onClick={runNow}
              disabled={executing || status?.isRunning}
              className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                executing || status?.isRunning
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-purple-600 text-white hover:bg-purple-700'
              }`}
            >
              {executing || status?.isRunning ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  {executing ? 'Uruchamianie...' : 'Optymalizacja w trakcie...'}
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  Uruchom teraz
                </>
              )}
            </button>
            {status?.isRunning && (
              <p className="mt-2 text-xs text-gray-500">
                Optymalizacja może potrwać kilka minut. Możesz zamknąć to okno.
              </p>
            )}
          </div>
        </div>

        {/* Recent Optimizations */}
        {status?.recentOptimizations && status.recentOptimizations.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Ostatnie optymalizacje</h4>
            <div className="space-y-2">
              {status.recentOptimizations.map((opt, index) => (
                <div
                  key={index}
                  className="p-3 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-900">
                      {formatDate(opt.timestamp)}
                    </span>
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        opt.status === 'SUCCESS'
                          ? 'bg-green-100 text-green-700'
                          : opt.status === 'PARTIAL'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {opt.status === 'SUCCESS' && <CheckCircle2 className="w-3 h-3" />}
                      {opt.status === 'PARTIAL' && <AlertCircle className="w-3 h-3" />}
                      {opt.status === 'ERROR' && <AlertCircle className="w-3 h-3" />}
                      {opt.status === 'SUCCESS' ? 'Sukces' : opt.status === 'PARTIAL' ? 'Częściowy' : 'Błąd'}
                    </span>
                  </div>
                  
                  {opt.details && (
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div>
                        <span className="text-gray-500">Przeanalizowano:</span>
                        <span className="ml-1 font-medium">{opt.details.audited || 0}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Rekomendacji:</span>
                        <span className="ml-1 font-medium">{opt.details.recommendationsGenerated || 0}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Wdrożono:</span>
                        <span className="ml-1 font-medium text-green-600">
                          {opt.details.recommendationsApplied || 0}
                        </span>
                      </div>
                    </div>
                  )}

                  {opt.details?.changes && opt.details.changes.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <p className="text-xs font-medium text-gray-600 mb-1">Wprowadzone zmiany:</p>
                      <ul className="space-y-1">
                        {opt.details.changes.slice(0, 3).map((change, i) => (
                          <li key={i} className="text-xs text-gray-600">
                            • <span className="font-medium">{change.campaign}:</span> {change.description}
                          </li>
                        ))}
                        {opt.details.changes.length > 3 && (
                          <li className="text-xs text-gray-500 italic">
                            ...i {opt.details.changes.length - 3} więcej
                          </li>
                        )}
                      </ul>
                    </div>
                  )}

                  {opt.details?.errors && opt.details.errors.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-red-200">
                      <p className="text-xs font-medium text-red-600 mb-1">Błędy:</p>
                      <ul className="space-y-1">
                        {opt.details.errors.slice(0, 2).map((error, i) => (
                          <li key={i} className="text-xs text-red-600">• {error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Info */}
        <div className="p-4 bg-blue-50 rounded-lg">
          <div className="flex gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-700">
              <p className="font-medium mb-1">Jak działa automatyczna optymalizacja?</p>
              <ul className="space-y-1 text-xs">
                <li>• Analizuje wydajność wszystkich kampanii i słów kluczowych</li>
                <li>• Generuje rekomendacje oparte na danych z ostatnich 30 dni</li>
                <li>• Automatycznie wdraża zmiany z wysoką pewnością (&gt;80%)</li>
                <li>• Wysyła raport email z podsumowaniem na lukasz.sikorra@gmail.com</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutomationPanel;
